import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

export function DinoRun({ onScore }) {
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [score, setScore] = useState(0);
  const [y, setY] = useState(0);
  const [obstacles, setObstacles] = useState([]);
  const vY = useRef(0);
  const raf = useRef(null);
  const speed = useRef(6);
  const reported = useRef(false);
  const lastSpawn = useRef(0);

  const jump = useCallback(() => {
    setY((cur) => { if (cur === 0) { vY.current = 13; return 0.0001; } return cur; });
  }, []);

  const start = () => {
    setScore(0); setObstacles([]); setY(0); vY.current = 0; speed.current = 6;
    setOver(false); setRunning(true); reported.current = false; lastSpawn.current = 0;
  };

  useEffect(() => {
    if (!running) return;
    let frame = 0; let sc = 0; let obs = [];
    const loop = () => {
      frame++;
      // jump physics
      setY((cur) => {
        let ny = cur + vY.current;
        vY.current -= 0.9;
        if (ny <= 0) { ny = 0; vY.current = 0; }
        return ny;
      });
      speed.current = 6 + sc / 200;
      // spawn
      if (frame - lastSpawn.current > 60 + Math.random() * 50) {
        lastSpawn.current = frame; obs = [...obs, { x: 600, h: 24 + Math.random() * 20 }];
      }
      obs = obs.map((o) => ({ ...o, x: o.x - speed.current })).filter((o) => o.x > -30);
      setObstacles(obs);
      sc += 1; if (sc % 2 === 0) setScore(Math.floor(sc / 2));
      // collision: dino at x ~ 40-70, ground
      setY((cy) => {
        for (const o of obs) {
          if (o.x < 70 && o.x > 20 && cy < o.h) { setRunning(false); setOver(true); setScore(Math.floor(sc / 2)); }
        }
        return cy;
      });
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    const key = (e) => { if (e.code === "Space" || e.code === "ArrowUp") { e.preventDefault(); jump(); } };
    window.addEventListener("keydown", key);
    return () => { cancelAnimationFrame(raf.current); window.removeEventListener("keydown", key); };
  }, [running, jump]);

  useEffect(() => { if (over && !reported.current) { reported.current = true; onScore?.(score); } }, [over, score, onScore]);

  return (
    <div className="flex flex-col items-center w-full" data-testid="dino-run">
      <p className="font-mono text-neon-cyan text-lg mb-3" data-testid="dino-score">SCORE: {score}</p>
      <div onClick={jump} className="relative w-full max-w-xl bg-zinc-950 border-2 border-zinc-700 overflow-hidden cursor-pointer"
        style={{ height: 180 }}>
        <div className="absolute left-0 right-0 border-t-2 border-zinc-700" style={{ bottom: 30 }} />
        {/* dino */}
        <div className="absolute w-7 h-9 bg-neon-cyan rounded-sm" style={{ left: 40, bottom: 30 + y }} />
        {obstacles.map((o, i) => (
          <div key={i} className="absolute bg-neon-pink rounded-sm" style={{ left: o.x, bottom: 30, width: 18, height: o.h }} />
        ))}
        {!running && (
          <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-3">
            {over && <p className="font-display font-black text-2xl text-neon-pink uppercase" data-testid="dino-over">Crashed! {score}</p>}
            <Button onClick={(e) => { e.stopPropagation(); start(); }} data-testid="dino-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {over ? "Run Again" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Press SPACE / ↑ / tap to jump over obstacles.</p>
    </div>
  );
}
